// src/utils/fetchJourneys.ts

import { getApi, getApiLanguage } from './api';
import { TaggedJourney, mapJourneysData, Journey } from './mapJourneysData';

export type FetchJourneysParams = {
    type?: 'earlier' | 'later' | 'initial';
    originId: string;
    destinationId: string;
    date: string;
    tripType: 'departure' | 'arrival';
    options: Record<string, any>;
    earlierThan: string | null;
    laterThan: string | null;
    existingJourneys?: Journey[];
    onJourneysFetched: (journeys: TaggedJourney[], newEarlierThan: string | null, newLaterThan: string | null) => void;
    onError: (message: string) => void;
    onLoadingChange: (isLoading: boolean) => void;
};

export const fetchJourneys = ({
    type = 'initial',
    originId,
    destinationId,
    date,
    tripType,
    options,
    earlierThan,
    laterThan,
    existingJourneys = [],
    onJourneysFetched,
    onError,
    onLoadingChange,
  }: FetchJourneysParams) => {
    
  // Start loading
  onLoadingChange(true);

  getApi('journeys', {
    type,
    from: originId,
    to: destinationId,
    [tripType]: date,
    earlierThan,
    laterThan,
    language: getApiLanguage(),
    ...options
  })
    .then((data) => {
      const { earlierThan: newEarlierThan, laterThan: newLaterThan, journeys: newJourneys } = data;

      // Check if journeys is an array
      if (!Array.isArray(newJourneys)) {
        throw new Error('Invalid response format from server');
      }

      // Check if journeys array is empty (for initial searches)
      if (type === 'initial' && newJourneys.length === 0) {
        onError('no-journeys');
        onLoadingChange(false);
        return;
      }

      // Map journeys using the existing journeys passed as parameter
      const mappedJourneys = mapJourneysData(newJourneys as Journey[], type === 'initial' ? [] : existingJourneys);

      // Notify the parent component of the fetched data
      onJourneysFetched(mappedJourneys, newEarlierThan, newLaterThan);
    })
  .catch((error) => {
      // Store the original error message for display in small text
      const originalErrorMessage = error.message || 'Unknown error occurred';
      
      // Provide user-friendly error messages for the subtitle
      let userFriendlyMessage = 'We couldn\'t find any journeys for your search. Please try different stations or times.';
      let errorType: 'error' | 'no-journeys' = 'error';
      
      if (error.message) {
        // If it's a known error type, use it; otherwise use generic message
        if (error.message === 'no-journeys') {
          errorType = 'no-journeys';
          userFriendlyMessage = error.message;
        } else if (error.message.includes('Failed to retrieve journey data') || error.message.includes('Server error')) {
          userFriendlyMessage = 'We\'re having trouble connecting to our servers. Please try again in a moment.';
        } else if (error.message.includes('Invalid response format')) {
          userFriendlyMessage = 'We received an unexpected response. Please try again.';
        }
      }
      
      // Pass both the user-friendly message and the original error message
      // Format: "userFriendlyMessage|originalErrorMessage"
      onError(`${userFriendlyMessage}|${originalErrorMessage}`);
    })
    .finally(() => {
      onLoadingChange(false);
  });
}
